import React from 'react';

import castArray from 'lodash/castArray';

import {
    SelectTextField,
    type SingleSelectTextFieldProps,
    type MultipleSelectTextFieldProps,
} from '@lumapps/combobox/components/SelectTextField';
import type { QueryInstanceSearchOptions } from '@lumapps/instance/api/types';
import { useQueryInstancesByIds } from '@lumapps/instance/hooks/useQueryInstancesByIds';
import type { Instance } from '@lumapps/instance/types';
import type { Fields } from '@lumapps/utils/types/Fields';

import { useInstancePickerState } from '../../hooks/useInstancePickerState';

export interface BaseInstancePickerFieldProps {
    /**
     * Additional search params
     */
    searchParams?: QueryInstanceSearchOptions;
    /**
     * list of instances ids to hide from the picker
     */
    instancesToExclude?: string[];
}

export type SingleInstancePickerFieldProps = {
    selectionType: SingleSelectTextFieldProps['selectionType'];
    /** Currently selected instance */
    value?: Instance | string;
    /** Callback on selection change */
    onChange?: SingleSelectTextFieldProps<Instance>['onChange'];
    /** Additional props for the select field component */
    selectTextFieldProps?: Partial<Omit<SingleSelectTextFieldProps<Instance>, 'selectionType' | 'value' | 'onChange'>>;
};
export type MultipleInstancePickerFieldProps = {
    selectionType: MultipleSelectTextFieldProps['selectionType'];
    /** Currently selected instance */
    value?: Array<Instance | string>;
    /** Callback on selection change */
    onChange?: MultipleSelectTextFieldProps<Instance>['onChange'];
    /** Additional props for the select field component */
    selectTextFieldProps?: Partial<
        Omit<MultipleSelectTextFieldProps<Instance>, 'selectionType' | 'value' | 'onChange'>
    >;
};

export type InstancePickerFieldProps = BaseInstancePickerFieldProps &
    (SingleInstancePickerFieldProps | MultipleInstancePickerFieldProps);

const MINIMAL_FIELDS: Fields<Instance> = ['id', 'name'];

/**
 * Select (single or multiple) instance picker field
 *
 * @family Pickers
 * @param InstancePickerFieldProps
 * @returns InstancePickerField
 */
export const InstancePickerField: React.FC<InstancePickerFieldProps> = ({
    selectionType,
    value,
    onChange,
    selectTextFieldProps,
    searchParams,
    instancesToExclude,
}) => {
    const {
        sites,
        status: searchStatus,
        onSearch,
        onInfiniteScroll,
        onFocus,
    } = useInstancePickerState(searchParams, instancesToExclude);

    // Fetching selected instances if needed
    const valueArray = castArray(value).filter(Boolean);
    const queryInstances = useQueryInstancesByIds(valueArray, {
        fields: searchParams?.itemFields || MINIMAL_FIELDS,
    });

    // We disable the component if we are currently fetching the provided instances.
    const isSelectDisabled = Boolean(valueArray.length && queryInstances.isLoading);

    // Adapt props based on the selection type
    const selectionProps =
        selectionType === 'single'
            ? {
                  selectionType,
                  value: queryInstances.items?.[0],
                  onChange,
                  ...selectTextFieldProps,
              }
            : {
                  selectionType,
                  value: queryInstances.items?.filter(Boolean) as Instance[],
                  onChange,
                  ...selectTextFieldProps,
              };

    return (
        <SelectTextField<Instance>
            autoFilter={false}
            getOptionId="id"
            getOptionName="name"
            {...selectionProps}
            onSearch={onSearch}
            isDisabled={selectTextFieldProps?.isDisabled || isSelectDisabled}
            onLoadMore={onInfiniteScroll}
            options={sites}
            status={searchStatus}
            onFocus={onFocus}
        />
    );
};
